import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Tooltip } from '@mui/material';

import { FONT_FAMILY } from '@/shared/ui/form-fields/fieldStyles';

import MapControlButton from './MapControlButton';
import CancelXIcon from './icons/CancelXIcon';
import ConfirmCheckIcon from './icons/ConfirmCheckIcon';
import MapPinCenterIcon from './icons/MapPinCenterIcon';
import UndoIcon from './icons/UndoIcon';
import ZoomInIcon from './icons/ZoomInIcon';
import ZoomOutIcon from './icons/ZoomOutIcon';

interface Props {
  onEnterPlacementMode: () => void;
  onCancelPlacement?: () => void;
  onUndo?: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onConfirm?: () => void;
  placementModeActive: boolean;
  hasPendingPin: boolean;
  isSaving: boolean;
  spaceId?: string;
  readOnly?: boolean;
}

const islandSx = {
  display: 'inline-flex',
  padding: '8px 12px',
  flexDirection: 'column' as const,
  alignItems: 'flex-start',
  borderRadius: '9999px',
  background: 'rgba(255, 255, 255, 0.80)',
  boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.15)',
  backdropFilter: 'blur(2px)',
};

const textButtonSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  height: '36px',
  borderRadius: '9999px',
  border: 'none',
  outline: 'none',
  cursor: 'pointer',
  background: 'transparent',
  fontFamily: FONT_FAMILY,
  fontSize: '12px',
  lineHeight: 1,
  color: '#010203',
  px: '16px',
  '&:hover': { background: 'rgba(0,0,0,0.06)' },
  '&:focus-visible': { outline: 'none', boxShadow: 'none' },
  '&.Mui-focusVisible': { outline: 'none', boxShadow: 'none' },
};

const confirmButtonSx = {
  ...textButtonSx,
  px: '8px',
};

const MapControlsIsland: FC<Props> = ({
  onEnterPlacementMode,
  onCancelPlacement,
  onUndo,
  onZoomIn,
  onZoomOut,
  onConfirm,
  placementModeActive,
  hasPendingPin,
  isSaving,
  spaceId,
  readOnly = false,
}) => {
  const { t } = useTranslation();

  const confirmDisabled = isSaving;
  const confirmTooltip = !spaceId
    ? t('spaces_form.position_on_map.deferred_save_tooltip')
    : t('spaces_form.position_on_map.confirm_placement');

  if (!readOnly && hasPendingPin) {
    return (
      <Box sx={islandSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <MapControlButton
            data-testid="floor-plan-undo-button"
            onClick={() => onUndo?.()}
            tooltip={t('spaces_form.position_on_map.undo_placement')}
          >
            <UndoIcon />
          </MapControlButton>

          <MapControlButton onClick={onZoomIn} tooltip={t('spaces_form.position_on_map.zoom_in')}>
            <ZoomInIcon />
          </MapControlButton>

          <MapControlButton onClick={onZoomOut} tooltip={t('spaces_form.position_on_map.zoom_out')}>
            <ZoomOutIcon />
          </MapControlButton>

          <Tooltip title={confirmTooltip} placement="top">
            <span>
              <Box
                component="button"
                data-testid="floor-plan-confirm-button"
                onClick={confirmDisabled ? undefined : () => onConfirm?.()}
                disabled={confirmDisabled}
                sx={{
                  ...confirmButtonSx,
                  opacity: confirmDisabled ? 0.4 : 1,
                  cursor: confirmDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <ConfirmCheckIcon />
                {t('spaces_form.position_on_map.confirm_placement')}
              </Box>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  if (!readOnly && placementModeActive) {
    return (
      <Box sx={islandSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title={t('spaces_form.position_on_map.cancel_placement')} placement="top">
            <span>
              <Box
                component="button"
                data-testid="floor-plan-cancel-button"
                onClick={() => onCancelPlacement?.()}
                sx={textButtonSx}
              >
                <CancelXIcon />
                {t('spaces_form.position_on_map.cancel_placement')}
              </Box>
            </span>
          </Tooltip>

          <MapControlButton onClick={onZoomIn} tooltip={t('spaces_form.position_on_map.zoom_in')}>
            <ZoomInIcon />
          </MapControlButton>

          <MapControlButton onClick={onZoomOut} tooltip={t('spaces_form.position_on_map.zoom_out')}>
            <ZoomOutIcon />
          </MapControlButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={islandSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {!readOnly && (
          <MapControlButton
            data-testid="floor-plan-enter-placement-button"
            onClick={onEnterPlacementMode}
            tooltip={t('spaces_form.position_on_map.enter_placement_mode')}
          >
            <MapPinCenterIcon />
          </MapControlButton>
        )}

        <MapControlButton onClick={onZoomIn} tooltip={t('spaces_form.position_on_map.zoom_in')}>
          <ZoomInIcon />
        </MapControlButton>

        <MapControlButton onClick={onZoomOut} tooltip={t('spaces_form.position_on_map.zoom_out')}>
          <ZoomOutIcon />
        </MapControlButton>
      </Box>
    </Box>
  );
};

export default MapControlsIsland;
